import { screen, colors } from 'components/styles/variables';
import { useTranslation } from 'react-i18next';
import { useContext } from 'react';
import { trackDealerPagesClick } from 'lib/Tracking/DealerPages';
import { DealerPagesEventType } from 'lib/EventSchema';
import TrackingDataContext from 'contexts/TrackingDataContext';
import StaticMap from 'components/StaticMap';
import { resolve } from 'styled-jsx/css';
import { DealerAddress } from 'lib/models/Dealer';
import { DealerHours } from 'lib/models/DealerHours';
import { Call, Email, Directions, Website } from './ContactInfo';
import Share from './Share';
import Hours from './Hours';
import CTAButtons from './CTAButtons';

function AutolistContact(): JSX.Element {
  const { t } = useTranslation();
  const { dealerPageUrl } = useContext(TrackingDataContext);

  return (
    <div className="contact">
      <h4 className="dealer-contact-questions">{t('dealerPages.questions')}</h4>
      <a
        title="Contact Autolist"
        href="mailto:help@autolist.com?subject=Autolist%20Inquiry"
        data-testid="contact-link"
        className="contact-dealer-link"
        onClick={(): void =>
          trackDealerPagesClick({
            type: DealerPagesEventType.contact_autolist_click,
            dealerPageUrl,
          })
        }
      >
        {t('dealerPages.contactAutolist')}
      </a>
      <style jsx>{`
        .contact {
          display: flex;
          padding-left: 5px;
          justify-content: flex-start;
          margin-left: 28px;
          margin-bottom: 55px;
        }

        .contact .dealer-contact-questions {
          margin: 0;
          padding: 0;
          margin-right: 8px;
          color: ${colors.G4};
          font-weight: 500;
        }
        .contact .contact-dealer-link {
          font-weight: 500;
          width: 205px;
        }

        .contact a:hover {
          font-weight: 600;
          color: ${colors.ALR2};
        }
      `}</style>
    </div>
  );
}

/* istanbul ignore next */
const staticMapCSS = resolve`
margin-bottom: 15px;

@media screen and (min-width: ${screen.smMin}) {
    margin: 0;
    position: initial;
}
`;

interface DealerContactProps {
  name: string;
  phone?: string;
  email?: string;
  address: DealerAddress;
  hours?: DealerHours;
  website: string;
  websiteName: string;
  dealerPageUrl: string;
  mapUrl?: string;
}

export default function DealerContact({
  name,
  phone,
  email,
  website,
  websiteName,
  address,
  hours,
  dealerPageUrl,
  mapUrl,
}: DealerContactProps): JSX.Element {
  const { isValid: addressIsValid, fullAddress, street, city, state, zip } = address;
  const map = mapUrl && (
    <StaticMap
      address={fullAddress}
      className={staticMapCSS.className}
      url={mapUrl}
      onClick={(): void =>
        trackDealerPagesClick({
          type: DealerPagesEventType.google_maps_click,
          dealerPageUrl,
          targetPageUrl: mapUrl,
        })
      }
    />
  );

  return (
    <div className="dealer-contact">
      <div className="left">
        {map}
        <CTAButtons />
      </div>
      <div className="right">
        <div className="actions">
          {phone && <Call phone={phone} />}
          {email && <Email email={email} />}
          {addressIsValid && <Directions street={street} city={city} state={state} zip={zip} />}
          {website && <Website site={website} siteName={websiteName} />}
          <Share slug={dealerPageUrl} title={name} />
        </div>
        <hr className="divider" />
        <div>
          {hours && <Hours hours={hours} />}
          <AutolistContact />
        </div>
      </div>

      <style jsx>{`
        .dealer-contact {
          padding: 0px 0px 0px 5px;
          display: flex;
          flex-direction: column;
          width: 100%;
        }

        @media screen and (min-width: ${screen.smMin}) {
          .dealer-contact {
            flex-direction: row;
          }
        }

        .right {
          display: flex;
          flex-direction: column;
        }

        @media screen and (min-width: ${screen.smMin}) {
          .right {
            margin-left: 20px;
          }
        }

        @media screen and (min-width: ${screen.mdMin}) {
          .right {
            flex-direction: row;
          }
        }

        .divider {
          display: none;
        }

        @media (min-width: ${screen.mdMin}) {
          .divider {
            display: initial;
            width: 1px;
            align-self: stretch;
            /* Bootstrap overrides */
            height: auto;
            max-height: 235px;
            border: 0;
            border-top: none;
            box-sizing: border-box;
            background-color: ${colors.G2};
            margin: 0 37px 0 37px;
          }
        }

        .contact {
          margin-left: 65px;
          margin-bottom: 25px;
          display: flex;
          justify-content: flex-start;
        }

        .contact h4 {
          margin: 0;
          padding: 0;
          margin-right: 8px;
          color: ${colors.G4};
          font-weight: 500;
        }

        .contact .contact-dealer-link {
          font-weight: 500;
          width: 205px;
        }

        .contact a:hover {
          font-weight: 600;
          color: ${colors.ALR2};
        }
      `}</style>
      {staticMapCSS.styles}
    </div>
  );
}
